import { STATE } from 'app/constants';
import { ActionDispatching } from 'app/types-shared';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess, getErrorMessage } from 'app/area/shared/actions-utils';
import { api as codeSelectorApi } from 'app/area/code-selector/api';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { codetableAdministrationTranslation as text } from '../constants';
import { api, codetableOptionsActionCreators, LimitFieldsTo } from '../common';
import { api as commonApi } from '../common/api';
import { codeFieldIncludeInactive } from '../common/codetable-options-handler/constants';
import { ScreenPicker } from '../item/screen-renderer/types';
import { mapCodeItemAccess } from '../item/mapping/map-code-item';
import { mapListToStore } from './mapping/map-list-to-store';
import {
  RequestCodeItems, ReceiveCodeItems, FailCodeItems,
  ExpandCodeItem, InvalidateCodeItems,
  UpdateCategory, UpdateQuery, FailCodetableAdministrationList,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  RequestCodetableAdministrationList, ReceiveCodetableAdministrationList, UpdateIncludeInactive,
  ClearCodesAccess, RequestCodesAccess, ReceiveCodesAccess, FailCodesAccess, UpdateSearchModel,
  ResetListState as ResetListState, UpdateMergeCode, RequestCodeParent, ReceiveCodeParent, FailCodeParent,
  UpdateValidation, ResetValidation, UpdateConfirmPrompt
} from './action-types';
import { CodeFieldWireKey, CodeFieldFlagWireKey, CodeFieldIncludeInactiveKey, CodetableFeature, ConfirmPromptState } from '../types-store';
import { CodeComplexValue } from '../../shared/codetable/types';
import { defaultIncludeInactiveCodes, defaultMaxResults } from './constants';
import { DeleteCodeItemPayload, LoadCodeItemsPayload } from './types-store';
import { MergeCodeState } from 'app/area/codetable/common/merge-code/types-store';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';

export const actionCreators = {

  ...codetableOptionsActionCreators,

  updateSearchModel: (model: FilterSearchModel): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateSearchModel(model));
    },

  loadCodetableAdministrationList: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestCodetableAdministrationList());

      try {
        const codetables = await codeSelectorApi.codetableAdministrationList();

        const codeItems = codetables.rootCodes ? codetables.rootCodes.map(codeItemToStoreValue) : [];

        dispatch(new ReceiveCodetableAdministrationList(codeItems));
      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodetableAdministrationList());
      }
    },

  resetListState: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {
      dispatch(new ResetListState());
      dispatch(new UpdateIncludeInactive(CodeFieldIncludeInactiveKey.active));
    },

  loadCodeItems: (payload: LoadCodeItemsPayload):
    ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      const { codetableId, parentId, categoryId, startIndex = 0, maxResults = defaultMaxResults,
        includeInactiveCodes = codeFieldIncludeInactive[defaultIncludeInactiveCodes],
        ignoreCategoryFilters, searchModel = { query: { text: '' } }, shouldLoadCodeItems = true, limitFieldsTo } = payload;

      dispatch(new RequestCodeItems(codetableId, parentId, categoryId, searchModel));

      if (!shouldLoadCodeItems) {
        dispatch(new ReceiveCodeItems(codetableId, parentId, categoryId, searchModel, [], 0, startIndex === 0));
        return;
      }

      const defaultLimitFieldsTo: LimitFieldsTo = [
        CodeFieldWireKey.code,
        CodeFieldWireKey.category,
        CodeFieldWireKey.node,
        CodeFieldFlagWireKey.colour,
        CodeFieldFlagWireKey.icon,
        CodetableFeature.parent,
        ScreenPicker.likelihood,
        ScreenPicker.recurrence,
        ScreenPicker.severity,
        payload.isMetadataSequence && ScreenPicker.sequence
      ].filter(f => !!f);

      try {
        const codeItemsResponse = await api.loadCodeItems({
          id: codetableId,
          parentId,
          categoryId,
          model: searchModel,
          startIndex,
          maxResults,
          displayNotSelected: true,
          limitFieldsTo: limitFieldsTo ?? defaultLimitFieldsTo,
          includeInactiveCodes,
          ignoreCategoryFilters
        });

        const codeItems = mapListToStore(codeItemsResponse.results);
        const numberOfResults = codeItemsResponse.totalNumberOfResults;
        const clearList = startIndex === 0;

        dispatch(new ReceiveCodeItems(codetableId, parentId, categoryId, searchModel, codeItems, numberOfResults, clearList));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodeItems(codetableId, parentId, categoryId, searchModel));
      }
    },

  deleteCodeItem: (payload: DeleteCodeItemPayload): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const { codeId, successMessage, codetableId, reassignReferencesToCode, successAction } = payload;

      dispatch(new RequestCodeItemDelete(codetableId, codeId));
      try {
        await commonApi.deleteCodeItem(codetableId, codeId, reassignReferencesToCode);

        dispatch(new ReceiveCodeItemDelete(codeId));
        dispatch(new SuccessNotification(successMessage));
        successAction && successAction();
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCodeItemDelete(codeId));

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  clearCodesAccess: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new ClearCodesAccess());
    },

  loadCodesAccess: (codetableId: number, codeId: number): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new RequestCodesAccess(codetableId, codeId));

      try {
        const result = await api.loadCodesAccess(codetableId, codeId);
        const mappedCodesAccess = mapCodeItemAccess(codeId, result);

        dispatch(new ReceiveCodesAccess(mappedCodesAccess));

      } catch (err) {
        dispatch(new FailCodesAccess(codetableId, codeId));
      }
    },

  expandCodeItem: (parentId: number): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new ExpandCodeItem(parentId));
    },

  invalidateCodeItems: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new InvalidateCodeItems());
    },

  updateCaseCategory: (value: CodeComplexValue<number>): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateCategory(value));
    },

  updateQuery: (value: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateQuery(value));
    },

  updateIncludeInactive: (value: CodeFieldIncludeInactiveKey): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateIncludeInactive(value));
    },

  updateMergeCode: (mergeCode: MergeCodeState): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateMergeCode(mergeCode));
    },

  updateConfirmPrompt: (confirmPrompt: ConfirmPromptState) => (dispatch: any, getState: any) => {
    dispatch(new UpdateConfirmPrompt(confirmPrompt));
  },

  updateCodeParent: (codetableId: number, codeId: number, parentId: number, successMessage: string,
    loadCodetableList: (reload: boolean) => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCodeParent());

      try {
        await api.updateParent(codetableId, codeId, parentId);
        loadCodetableList(true);
        dispatch(new ReceiveCodeParent());
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {
        if (isForbiddenStatus(err)) {
          dispatch(
            new ErrorNotification(
              getErrorMessage({
                dispatch,
                getState,
                area: STATE.administrationCodetable,
                translationId: text.update_code_parent_error
              })
            ));
        } else if (err?.userMessages || err?.validationErrors) {
          const validationErrorMessage = err?.validationErrors?.codeId?.[0]?.message
            ?? err?.validationErrors?.parent?.[0]?.message;
          const userMessage = err.userMessages?.[0]?.message;
          dispatch(new ErrorNotification(validationErrorMessage || userMessage));
          dispatch(new FailCodeParent());
        }
      }
    },

  resetValidation: (key: ScreenPicker | string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  }
};
